import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';

const UploadModal = ({ onClose, onUpload }) => {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [caseAssignment, setCaseAssignment] = useState('');
  const [documentCategory, setDocumentCategory] = useState('');
  const [tags, setTags] = useState('');
  const fileInputRef = useRef(null);

  const mockCases = [
    { id: 'CASE-2024-001', name: 'Mwangi vs Kamau' },
    { id: 'CASE-2024-002', name: 'Njoroge Property Transfer' },
    { id: 'CASE-2024-003', name: 'Employment Dispute - Safaricom' },
    { id: 'CASE-2024-004', name: 'Constitutional Petition No. 123' }
  ];

  const documentCategories = [
    'Court Filing',
    'Legal Document',
    'Contract',
    'Agreement',
    'Evidence',
    'Correspondence',
    'Research',
    'Template'
  ];

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file) => {
    const extension = file.name.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf': return 'FileText';
      case 'doc': case'docx': return 'FileText';
      case 'xls': case'xlsx': return 'FileSpreadsheet';
      case 'jpg': case'jpeg': case'png': case'gif': return 'Image';
      case 'zip': case'rar': return 'Archive';
      default: return 'File';
    }
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) return;

    // Simulate upload progress
    selectedFiles.forEach((file, index) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 30;
        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);
        }
        setUploadProgress(prev => ({
          ...prev,
          [index]: progress
        }));
      }, 200);
    });

    // Complete upload after 3 seconds
    setTimeout(() => {
      onUpload(selectedFiles);
    }, 3000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-dialog w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-heading font-semibold text-text-primary">Upload Documents</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-smooth ${
              dragOver ? 'border-primary bg-primary-50' : 'border-border hover:border-primary hover:bg-primary-50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Icon name="Upload" size={48} className="mx-auto text-primary mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">
              Drop files here or click to browse
            </h3>
            <p className="text-text-secondary mb-4">
              Supports PDF, Word, Excel, images, and archive files up to 50MB each
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-primary text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-smooth"
            >
              Choose Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip,.rar"
            />
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="mt-6">
              <h4 className="font-medium text-text-primary mb-4">Selected Files ({selectedFiles.length})</h4>
              <div className="space-y-3 max-h-48 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                    <div className="flex items-center flex-1 min-w-0">
                      <Icon name={getFileIcon(file)} size={20} className="text-primary mr-3 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">{file.name}</p>
                        <p className="text-xs text-text-secondary">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    
                    {uploadProgress[index] !== undefined ? (
                      <div className="flex items-center ml-4">
                        <div className="w-20 bg-secondary-200 rounded-full h-2 mr-3">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress[index]}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-text-secondary w-10">
                          {Math.round(uploadProgress[index])}%
                        </span>
                      </div>
                    ) : (
                      <button
                        onClick={() => removeFile(index)}
                        className="ml-4 p-1 hover:bg-secondary-200 rounded transition-smooth"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Options */}
          {selectedFiles.length > 0 && (
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Assign to Case
                  </label>
                  <select
                    value={caseAssignment}
                    onChange={(e) => setCaseAssignment(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select a case (optional)</option>
                    {mockCases.map(case_ => (
                      <option key={case_.id} value={case_.id}>{case_.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Document Category
                  </label>
                  <select
                    value={documentCategory}
                    onChange={(e) => setDocumentCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select category</option>
                    {documentCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="e.g., contract, urgent, client-review"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-text-secondary hover:text-text-primary transition-smooth"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={selectedFiles.length === 0}
            className="bg-primary text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Upload {selectedFiles.length > 0 ? `${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}` : 'Files'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;